import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { IMAGES } from "@/data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "Execution over abstraction",
    text: "Technology plans only matter when someone shows up, racks the equipment, runs the cable and makes it work. LEDOS exists for that moment — the physical point where plans become working infrastructure.",
  },
  {
    num: "02",
    title: "Installation quality is the product",
    text: "A network is only as good as its terminations. A camera is only as good as its mounting and connectivity. We treat the details of installation — routing, labeling, testing, documentation — as the core of what we deliver, not an afterthought.",
  },
  {
    num: "03",
    title: "Responsive by design",
    text: "When business-critical technology goes down, response matters. Our field-support work is built around diagnosing efficiently, communicating clearly and getting equipment operational again.",
  },
  {
    num: "04",
    title: "Operational understanding",
    text: "We work in live business environments — retail floors, offices, facilities that cannot simply pause. We plan work around your operations and leave sites clean, documented and ready for handover.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "About — LEDOS Enterprise Solutions";
  }, []);

  return (
    <main data-testid="about-page">
      <section className="relative bg-ink pt-44 pb-24 md:pb-32 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-8">
              About LEDOS
            </p>
          </MaskedLine>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <MaskedLine delay={0.25}>We connect the physical</MaskedLine>
            <MaskedLine delay={0.38}>
              <span>
                technology business <span className="text-signal">relies upon.</span>
              </span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5} className="mt-10 max-w-2xl">
            <p className="text-base md:text-lg leading-relaxed text-zinc-400">
              LEDOS Enterprise Solutions is a practical, execution-oriented technology services
              company. We deploy, install, connect, configure and support the IT infrastructure
              and field technology that organizations depend on — networks, cabling, servers,
              ATMs, POS systems, cameras and digital displays.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-manifesto-section" className="bg-panel border-y border-line py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            index="A."
            eyebrow="How We Think"
            title="Four Working Principles"
          />
          <div className="grid md:grid-cols-2 gap-px bg-line border border-line">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.num} delay={(i % 2) * 0.1} className="bg-panel">
                <div
                  data-testid={`about-principle-${ch.num}`}
                  className="p-10 md:p-14 h-full hover:bg-surface transition-colors duration-500 group"
                >
                  <span className="font-display text-6xl font-black tracking-tighter text-outline-signal block mb-8">
                    {ch.num}
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-4 group-hover:text-signal transition-colors duration-500">
                    {ch.title}
                  </h3>
                  <p className="text-base leading-relaxed text-zinc-400">{ch.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading
              index="B."
              eyebrow="What We Are"
              title="A Field Technology Company, Not a Software Vendor"
            />
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-zinc-400 mb-6">
                LEDOS is not a software company, and not a consulting firm that leaves you with
                a report. Our value is hands-on: arriving at your location and successfully
                deploying real technology — the cabling in the walls, the hardware in the rack,
                the screen on the wall, the terminal on the counter.
              </p>
              <p className="text-base leading-relaxed text-zinc-400 mb-10">
                That focus shapes everything: how we scope projects, how we prepare sites, how
                we document our work and how we support it afterwards.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="about-cta-services"
                className="group inline-flex items-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-6 py-4 hover:-translate-y-1 transition-transform duration-300"
              >
                See What We Deploy
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="group">
            <div className="clip-frame relative">
              <img
                src={IMAGES.engineer}
                alt="Field engineer at work on technical equipment"
                className="w-full h-[55vh] md:h-[70vh] object-cover img-field"
              />
              <div className="absolute inset-0 bg-black/30" />
            </div>
            <div className="flex justify-between gap-4 mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              <span>Illustrative image — field technology work</span>
              <span className="text-signal shrink-0">Representative of service capabilities</span>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
